import { Chart } from "chart.js/auto";
import { displayToast, sendAppsignalError } from "../modules/utils";

export default class DoughnutChart {
  constructor(node) {
    this.element = node;
    this.chart = this.element.querySelector(".js-canvas-chart");
    this.totalValueElement = this.element.querySelector(".js-total-value");
    this.legendContainer = this.element.querySelector(".js-chart-labels");

    this.decimals = parseInt(this.element.dataset.decimals);
    this.totalHiddenLabels = 0;
    this.totalLabelsCount = 0;
    this.hideLabels = this.element.dataset.hideLabels === "false";

    this.fetchData();
  }

  fetchData() {
    this.element.classList.add("is-loading");

    fetch(this.element.dataset.chartUrl, {
      retries: 3,
      retryDelay: 1000,
    })
      .then((response) => {
        this.response = response;

        if (response.status === 200) {
          return response.json();
        }

        throw new Error("Something went wrong");
      })
      .then((data) => {
        this.colors = [];
        this.values = [];
        this.labels = [];
        this.formattedValues = [];

        Object.values(data).forEach((entryObj) => {
          const { label, colorCode, value, formattedValue } = entryObj;

          if (!this.hideLabels) {
            this.labels.push(label);
          }
          this.colors.push(colorCode);
          this.values.push(value);
          this.formattedValues.push(formattedValue);
        });

        this.initialize();
      })
      .catch((e) => {
        console.error(e);
        displayToast(this.element.dataset.errorMessage);
        sendAppsignalError(e, "DoughnutChart", {
          response: this.response,
        });
      })
      .finally(() => {
        this.element.classList.remove("is-loading");
      });
  }

  initialize() {
    if (!this.hideLabels) {
      this.totalValue = this.values.reduce((a, b) => a + b);
    } else {
      // Incase it's a percentage of the total value
      this.totalValue = this.values[0];
    }

    let formattedTotalValue = this.totalValue % 1 !== 0 ? this.totalValue.toFixed(2) : this.totalValue;
    this.totalValueElement.innerText = formattedTotalValue;

    this.chartObject = new Chart(this.chart, {
      type: "doughnut",
      data: {
        labels: this.labels,
        datasets: [
          {
            data: this.values,
            borderWidth: 2,
            backgroundColor: this.colors,
          },
        ],
      },
      options: {
        cutout: "84%",
        responsive: true,
        maintainAspectRatio: false,
        onHover: (event, chartElement) => {
          event.native.target.style.cursor = chartElement[0] ? "pointer" : "default";
        },
        plugins: {
          tooltip: {
            intersect: true,
            position: "nearest",
            mode: "index",
            backgroundColor: "#1f1f1f",
            titleMarginBottom: 0,
            bodyFont: { family: "Mukta", size: 16, weight: "normal" },
            caretSize: 12,
            cornerRadius: 8,
            padding: { x: 16, y: 12 },
            displayColors: false,
            bodySpacing: 0,
            animation: {
              duration: 0.15,
              easing: "easeInOutCirc",
            },
            callbacks: {
              title: () => null,
              afterTitle: (context) => {
                context.forEach((tooltip) => {
                  if (this.hideLabels) {
                    tooltip.formattedValue = this.formattedValues[tooltip.dataIndex];
                  } else {
                    tooltip.formattedValue = `${tooltip.label}: ${this.formattedValues[tooltip.dataIndex]}`;
                  }
                  tooltip.label = "";
                });
              },
            },
          },
          legend: {
            display: false,
          },
        },
      },
      plugins: [
        {
          afterUpdate: (chart) => {
            if (!this.legendContainer) {
              return;
            }

            this.totalLabelsCount = chart.data.labels.length;
            const ul = this.getOrCreateLegendList();

            // Remove old legend items
            while (ul.firstChild) {
              ul.firstChild.remove();
            }

            // Reuse the built-in legendItems generator
            var labels = chart.options.plugins.legend.labels.generateLabels(chart);

            labels.forEach((label) => {
              const li = document.createElement("li");
              li.classList = "label-li-wrapper";

              li.onclick = () => {
                // Pie and doughnut charts only have a single dataset and visibility is per label

                if (chart.getDataVisibility(label.index)) {
                  if (this.totalHiddenLabels === this.totalLabelsCount - 1) {
                    return;
                  }
                  this.totalHiddenLabels++;
                  this.totalValue = this.totalValue - this.values[label.index];
                } else {
                  this.totalHiddenLabels--;
                  this.totalValue = this.totalValue + this.values[label.index];
                }

                // If there are remaining decimals, show them max 2 else 0
                let decimals = this.totalValue.toFixed(2) % 1 !== 0 ? 2 : 0;

                this.totalValueElement.innerText = this.totalValue.toFixed(decimals);

                chart.toggleDataVisibility(label.index);
                chart.update();
              };

              // Color box
              const boxSpan = document.createElement("span");
              boxSpan.classList = "label-circle";
              boxSpan.style.background = label.fillStyle;

              // Text
              const textContainer = document.createElement("div");
              textContainer.classList = "label-text";
              textContainer.style.textDecoration = label.hidden ? "line-through" : "";

              const text = document.createTextNode(label.text);
              textContainer.appendChild(text);

              li.appendChild(boxSpan);
              li.appendChild(textContainer);
              ul.appendChild(li);
            });
          },
        },
      ],
    });
  }

  getOrCreateLegendList() {
    let listContainer = this.legendContainer.querySelector("ul");

    if (!listContainer) {
      listContainer = document.createElement("ul");
      listContainer.classList = "label-ul-wrapper";

      this.legendContainer.appendChild(listContainer);
    }

    return listContainer;
  }
}
